/**
 * TradeStack - Reports Page
 */

import React, { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Reports.css';

function Reports() {
  const { formatCurrency } = useConfig();
  const [activeReport, setActiveReport] = useState('profit-loss');
  const [year, setYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, year]);

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      switch (activeReport) {
        case 'profit-loss':
          response = await api.getProfitLossReport({ year });
          break;
        case 'tax-estimate':
          response = await api.getTaxEstimate({ year });
          break;
        case 'expenses-category':
          response = await api.getExpensesByCategory({ year });
          break;
        case 'subcontractor-1099':
          response = await api.get1099Report({ year });
          break;
        default:
          response = { data: {} };
      }
      setReportData(response.data);
    } catch (err) {
      setError('Failed to load report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const reports = [
    { id: 'profit-loss', name: 'Profit & Loss', icon: '📊' },
    { id: 'tax-estimate', name: 'Tax Estimate', icon: '💰' },
    { id: 'expenses-category', name: 'Expenses by Category', icon: '📈' },
    { id: 'subcontractor-1099', name: '1099 Report', icon: '📄' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Reports</h1>
        <div className="year-selector">
          <label>Year:</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        {reports.map(report => (
          <button
            key={report.id}
            className={`report-tab ${activeReport === report.id ? 'active' : ''}`}
            onClick={() => setActiveReport(report.id)}
          >
            <span className="tab-icon">{report.icon}</span>
            <span className="tab-name">{report.name}</span>
          </button>
        ))}
      </div>

      {/* Report Content */}
      <div className="report-content">
        {loading ? (
          <div className="loading-state">Loading report...</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : !reportData ? (
          <div className="empty-state">Select a report to view</div>
        ) : (
          <>
            {/* Profit & Loss Report */}
            {activeReport === 'profit-loss' && reportData && (
              <div className="profit-loss-report">
                <div className="report-section">
                  <h3>Income</h3>
                  <div className="report-line total">
                    <span>Total Income</span>
                    <span>{formatCurrency(reportData.income?.total || 0)}</span>
                  </div>
                </div>

                <div className="report-section">
                  <h3>Expenses</h3>
                  {reportData.expenses?.byCategory?.map(cat => (
                    <div key={cat.category} className="report-line">
                      <span>{cat.category}</span>
                      <span>{formatCurrency(parseFloat(cat.total))}</span>
                    </div>
                  ))}
                  <div className="report-line subtotal">
                    <span>Total Expenses</span>
                    <span>{formatCurrency(reportData.expenses?.total || 0)}</span>
                  </div>
                </div>

                <div className="report-section">
                  <h3>Mileage Deduction</h3>
                  <div className="report-line">
                    <span>{parseFloat(reportData.mileage?.miles || 0).toFixed(1)} miles</span>
                    <span>{formatCurrency(reportData.mileage?.deduction || 0)}</span>
                  </div>
                </div>

                <div className="report-section summary">
                  <div className="summary-line gross">
                    <span>Gross Income</span>
                    <span>{formatCurrency(reportData.summary?.grossIncome || 0)}</span>
                  </div>
                  <div className="summary-line">
                    <span>Total Deductions</span>
                    <span>({formatCurrency(reportData.summary?.totalDeductions || 0)})</span>
                  </div>
                  <div className="summary-line net">
                    <span>Net Profit</span>
                    <span className={reportData.summary?.netProfit >= 0 ? 'positive' : 'negative'}>
                      {formatCurrency(reportData.summary?.netProfit || 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tax Estimate Report */}
            {activeReport === 'tax-estimate' && reportData && (
              <div className="tax-estimate-report">
                <div className="report-section">
                  <h3>Business Income</h3>
                  <div className="report-line">
                    <span>Gross Income</span>
                    <span>{formatCurrency(reportData.businessIncome?.gross || 0)}</span>
                  </div>
                  <div className="report-line">
                    <span>Expenses</span>
                    <span>({formatCurrency(reportData.businessIncome?.expenses || 0)})</span>
                  </div>
                  <div className="report-line">
                    <span>Mileage Deduction</span>
                    <span>({formatCurrency(reportData.businessIncome?.mileageDeduction || 0)})</span>
                  </div>
                  <div className="report-line subtotal">
                    <span>Net Profit</span>
                    <span>{formatCurrency(reportData.businessIncome?.netProfit || 0)}</span>
                  </div>
                </div>

                <div className="report-section">
                  <h3>Tax Calculations</h3>
                  <div className="report-line">
                    <span>Self-Employment Tax</span>
                    <span>{formatCurrency(reportData.taxEstimate?.selfEmploymentTax || 0)}</span>
                  </div>
                  <div className="report-line">
                    <span>Federal Income Tax (on business)</span>
                    <span>{formatCurrency(reportData.taxEstimate?.federalOnBusiness || 0)}</span>
                  </div>
                  <div className="report-line">
                    <span>State Tax (estimated)</span>
                    <span>{formatCurrency(reportData.taxEstimate?.stateTax || 0)}</span>
                  </div>
                  <div className="report-line total">
                    <span>Total Estimated Tax</span>
                    <span>{formatCurrency(reportData.taxEstimate?.totalOnBusiness || 0)}</span>
                  </div>
                </div>

                <div className="tax-recommendation">
                  <h3>Recommendation</h3>
                  <div className="recommendation-card">
                    <div className="rec-item">
                      <span className="rec-label">Set Aside</span>
                      <span className="rec-value">
                        {formatCurrency(reportData.recommendation?.setAside || 0)}
                      </span>
                    </div>
                    <div className="rec-item">
                      <span className="rec-label">Effective Rate</span>
                      <span className="rec-value">
                        {reportData.recommendation?.percentOfProfit || 0}%
                      </span>
                    </div>
                    <div className="rec-item">
                      <span className="rec-label">Quarterly Payment</span>
                      <span className="rec-value">
                        {formatCurrency(reportData.recommendation?.quarterly || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Expenses by Category Report */}
            {activeReport === 'expenses-category' && reportData && (
              <div className="category-report">
                <div className="category-summary">
                  <div className="summary-item">
                    <span className="summary-label">Total Expenses</span>
                    <span className="summary-value">{formatCurrency(reportData.totals?.expenses || 0)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tax Deductible</span>
                    <span className="summary-value positive">{formatCurrency(reportData.totals?.deductible || 0)}</span>
                  </div>
                </div>

                <div className="category-breakdown">
                  {reportData.categories?.map(cat => (
                    <div key={cat.category} className="category-row">
                      <div className="category-info">
                        <span className="category-name">{cat.categoryName || cat.category}</span>
                        <span className="category-count">{cat.transaction_count} transactions</span>
                      </div>
                      <div className="category-amounts">
                        <span className="category-total">{formatCurrency(parseFloat(cat.total))}</span>
                        {cat.taxDeductible && (
                          <span className="category-deductible">
                            {formatCurrency(parseFloat(cat.deductible_amount))} deductible
                          </span>
                        )}
                      </div>
                      <div className="category-bar">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${(parseFloat(cat.total) / (reportData.totals?.expenses || 1)) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 1099 Report */}
            {activeReport === 'subcontractor-1099' && reportData && (
              <div className="report-1099">
                <div className="report-summary-cards">
                  <div className="summary-card">
                    <span className="card-value">{reportData.summary?.total || 0}</span>
                    <span className="card-label">Total Subcontractors</span>
                  </div>
                  <div className="summary-card warning">
                    <span className="card-value">{reportData.summary?.requiring1099 || 0}</span>
                    <span className="card-label">Require 1099</span>
                  </div>
                  <div className="summary-card danger">
                    <span className="card-value">{reportData.summary?.missingW9 || 0}</span>
                    <span className="card-label">Missing W-9</span>
                  </div>
                  <div className="summary-card">
                    <span className="card-value">{formatCurrency(reportData.summary?.totalPaid || 0)}</span>
                    <span className="card-label">Total Paid</span>
                  </div>
                </div>

                {reportData.alerts?.length > 0 && (
                  <div className="alerts">
                    {reportData.alerts.map((alert, i) => (
                      <div key={i} className="alert alert-warning">{alert}</div>
                    ))}
                  </div>
                )}

                <div className="subcontractor-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Tax ID</th>
                        <th>W-9</th>
                        <th>Amount Paid</th>
                        <th>1099 Required</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.subcontractors?.map(sub => (
                        <tr key={sub.id}>
                          <td>{sub.company_name}</td>
                          <td>{sub.contact_name}</td>
                          <td>{sub.tax_id ? '****' + sub.tax_id.slice(-4) : '-'}</td>
                          <td>
                            {sub.w9_on_file ? (
                              <span className="badge success">On File</span>
                            ) : (
                              <span className="badge danger">Missing</span>
                            )}
                          </td>
                          <td>{formatCurrency(parseFloat(sub.total_paid || 0))}</td>
                          <td>
                            {parseFloat(sub.total_paid || 0) >= 600 ? (
                              <span className="badge warning">Yes</span>
                            ) : (
                              <span className="badge">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Reports;
